import React, { useEffect, useState, createContext, useContext } from 'react';
export type BankSms = {
  id: string;
  bankName: string;
  raw: string;
  amount: number;
  reference: string;
  accountSuffix: string;
  receivedAt: number;
  manualMatchedOrderId?: string;
  verifiedOrderId?: string;
  dismissed?: boolean;
};
type Ctx = {
  messages: BankSms[];
  addMessage: (msg: Omit<BankSms, 'id' | 'receivedAt'>) => void;
  simulateIncoming: () => void;
  linkToOrder: (smsId: string, orderId: string) => void;
  markVerified: (smsId: string, orderId: string) => void;
  dismiss: (smsId: string) => void;
};
const BankSmsContext = createContext<Ctx | null>(null);
const SMS_KEY = 'mp_bank_sms';
const uid = () => Math.random().toString(36).slice(2, 9);
const seedMessages: BankSms[] = [{
  id: 'sms_1',
  bankName: 'CBE',
  raw: 'CBE: Credited with ETB 35.93 from SELAM TESFAYE. Ref FT24159GH7P2 to A/C ...6789 on 10/06/2026. Bal ETB 12,340.00',
  amount: 35.93,
  reference: 'FT24159GH7P2',
  accountSuffix: '6789',
  receivedAt: Date.now() - 5 * 60000
}, {
  id: 'sms_2',
  bankName: 'CBE',
  raw: 'CBE: Credited with ETB 150.00 from ABEBE KEBEDE. Ref FT99999XXXXX to A/C ...6789 on 10/06/2026. Bal ETB 12,490.00',
  amount: 150.0,
  reference: 'FT99999XXXXX',
  accountSuffix: '6789',
  receivedAt: Date.now() - 60 * 60000
}, {
  id: 'sms_3',
  bankName: 'CBE',
  raw: 'CBE: Credited with ETB 45.00 from UNKNOWN. Ref FT11111YYYYY to A/C ...6789 on 10/06/2026. Bal ETB 12,535.00',
  amount: 45.0,
  reference: 'FT11111YYYYY',
  accountSuffix: '6789',
  receivedAt: Date.now() - 120 * 60000
}];
function loadInitial<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}
export function BankSmsProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [messages, setMessages] = useState<BankSms[]>(() => loadInitial(SMS_KEY, seedMessages));
  useEffect(() => {
    try {
      window.localStorage.setItem(SMS_KEY, JSON.stringify(messages));
    } catch {
      /* ignore */
    }
  }, [messages]);
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === SMS_KEY && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          if (Array.isArray(parsed)) setMessages(parsed);
        } catch {
          /* ignore */
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  const addMessage = (msg: Omit<BankSms, 'id' | 'receivedAt'>) => {
    setMessages(prev => [{
      ...msg,
      id: `sms_${uid()}`,
      receivedAt: Date.now()
    }, ...prev]);
  };
  const simulateIncoming = () => {
    addMessage({
      bankName: 'CBE',
      raw: 'CBE: Credited with ETB 35.93 from SELAM TESFAYE. Ref FT24159GH7P2 to A/C ...6789 on 10/06/2026. Bal ETB 12,340.00',
      amount: 35.93,
      reference: 'FT24159GH7P2',
      accountSuffix: '6789'
    });
  };
  const linkToOrder = (smsId: string, orderId: string) => {
    setMessages(prev => prev.map(m => m.id === smsId ? {
      ...m,
      manualMatchedOrderId: orderId
    } : m));
  };
  const markVerified = (smsId: string, orderId: string) => {
    setMessages(prev => prev.map(m => m.id === smsId ? {
      ...m,
      verifiedOrderId: orderId
    } : m));
  };
  const dismiss = (smsId: string) => {
    setMessages(prev => prev.map(m => m.id === smsId ? {
      ...m,
      dismissed: true
    } : m));
  };
  return <BankSmsContext.Provider value={{
    messages,
    addMessage,
    simulateIncoming,
    linkToOrder,
    markVerified,
    dismiss
  }} data-id="element-375">
      {children}
    </BankSmsContext.Provider>;
}
export function useBankSms() {
  const ctx = useContext(BankSmsContext);
  if (!ctx) throw new Error('useBankSms must be used within BankSmsProvider');
  return ctx;
}